import React, { useEffect, useState } from 'react';

/**
 * Confetti celebration animation component.
 * Renders falling confetti pieces across the screen.
 * Uses on-brand colors: coral, yellow, graphite, wood tones
 */
export default function Confetti({ duration = 3000, onComplete }) {
  const [pieces, setPieces] = useState([]);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // On-brand confetti colors
    const colors = [
      '#f7c948', // yellow
      '#d4736a', // coral
      '#3d3d3d', // graphite
      '#c9a87c', // wood
      '#d4a832', // dark yellow
      '#e8a0a0', // light coral
      '#fffef9', // paper/off-white
    ];
    const newPieces = [];

    for (let i = 0; i < 150; i++) {
      newPieces.push({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 1000,
        duration: 2000 + Math.random() * 2000,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 8 + Math.random() * 8,
        rotation: Math.random() * 360,
        type: Math.random() > 0.5 ? 'circle' : 'rect',
      });
    }

    setPieces(newPieces);

    // Hide after duration
    const timer = setTimeout(() => {
      setVisible(false);
      onComplete?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onComplete]);

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'none',
      zIndex: 9999,
      overflow: 'hidden',
    }}>
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(-20px) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        @keyframes confetti-shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
        @keyframes celebration-pop {
          0% {
            transform: translate(-50%, -50%) scale(0);
            opacity: 0;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.1);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
        }
        @keyframes celebration-fade {
          0% {
            opacity: 1;
          }
          70% {
            opacity: 1;
          }
          100% {
            opacity: 0;
          }
        }
      `}</style>

      {pieces.map((piece) => (
        <div
          key={piece.id}
          style={{
            position: 'absolute',
            left: `${piece.left}%`,
            top: '-20px',
            width: piece.type === 'circle' ? piece.size : piece.size * 0.6,
            height: piece.size,
            backgroundColor: piece.color,
            borderRadius: piece.type === 'circle' ? '50%' : '2px',
            animation: `confetti-fall ${piece.duration}ms ease-out ${piece.delay}ms forwards`,
            transform: `rotate(${piece.rotation}deg)`,
            boxShadow: piece.color === '#fffef9' ? '0 0 2px rgba(0,0,0,0.1)' : 'none',
          }}
        />
      ))}

      {/* Celebration icon and text */}
      <div style={{
        position: 'absolute',
        top: '30%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center',
        animation: 'celebration-pop 0.5s ease-out forwards, celebration-fade 3s ease-out 2s forwards',
      }}>
        {/* On-brand celebration icon - sparkle/star burst */}
        <div style={{ marginBottom: '16px' }}>
          <svg width="80" height="80" viewBox="0 0 48 48" fill="none">
            {/* Central star */}
            <path
              d="M24 4 L26 14 L36 12 L28 20 L36 28 L26 26 L24 36 L22 26 L12 28 L20 20 L12 12 L22 14 Z"
              fill="#f7c948"
              stroke="#d4a832"
              strokeWidth="1"
            />
            {/* Inner glow */}
            <circle cx="24" cy="20" r="5" fill="#d4736a"/>
            {/* Sparkle accents */}
            <circle cx="38" cy="8" r="3" fill="#d4736a" opacity="0.8"/>
            <circle cx="10" cy="38" r="2.5" fill="#f7c948" opacity="0.8"/>
            <circle cx="40" cy="36" r="2" fill="#c9a87c" opacity="0.8"/>
            <circle cx="8" cy="12" r="2" fill="#d4736a" opacity="0.6"/>
            {/* Rays */}
            <path d="M24 0 L24 3" stroke="#f7c948" strokeWidth="2" strokeLinecap="round"/>
            <path d="M24 45 L24 48" stroke="#f7c948" strokeWidth="2" strokeLinecap="round"/>
            <path d="M0 24 L3 24" stroke="#d4736a" strokeWidth="2" strokeLinecap="round"/>
            <path d="M45 24 L48 24" stroke="#d4736a" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
        <div style={{
          fontSize: '24px',
          fontWeight: '700',
          color: '#3d3d3d',
          textShadow: '0 2px 10px rgba(255,255,255,0.8)',
          background: 'rgba(255,254,249,0.95)',
          padding: '12px 24px',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          border: '1px solid #e8e8e8',
        }}>
          Ready to land your next role
        </div>
      </div>
    </div>
  );
}
